import { RefObject, useLayoutEffect, useState } from 'react';

import { TabItem } from 'contexts/TabContext';

/** Matches the min-width on a record tab; the bar can only show whole tabs. */
const MIN_TAB_WIDTH = 120;

/** Room to reserve for the "N more" button once there is an overflow at all. */
const MORE_BUTTON_WIDTH = 84;

interface Overflow {
  visible: TabItem[];
  hidden: TabItem[];
}

/**
 * Splits record tabs into the ones that physically fit and the ones that go into the dropdown.
 *
 * Overflow is decided by measured width, never by a tab count, so the threshold moves on its own
 * across laptop screens, wide monitors and browser zoom levels (TAB-08). Before the first
 * measurement — and in any environment without layout — everything stays visible, which keeps the
 * bar honest rather than briefly hiding tabs it has not measured yet.
 */
const useTabOverflow = (containerRef: RefObject<HTMLElement>, pinnedRef: RefObject<HTMLElement>, records: TabItem[], activeKey: string): Overflow => {
  const [barWidth, setBarWidth] = useState(0);
  const [pinnedWidth, setPinnedWidth] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const measure = () => {
      setBarWidth(container.clientWidth);
      setPinnedWidth(pinnedRef.current ? pinnedRef.current.offsetWidth : 0);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, pinnedRef, records.length]);

  if (barWidth === 0) {
    return { visible: records, hidden: [] };
  }

  const available = Math.max(0, barWidth - pinnedWidth);
  let capacity = Math.floor(available / MIN_TAB_WIDTH);

  if (records.length > capacity) {
    capacity = Math.max(1, Math.floor((available - MORE_BUTTON_WIDTH) / MIN_TAB_WIDTH));
  }

  if (records.length <= capacity) {
    return { visible: records, hidden: [] };
  }

  const visible = records.slice(0, capacity);
  const hidden = records.slice(capacity);

  // TAB-08: the active tab is never the one pushed out of sight.
  const activeIndex = hidden.findIndex(tab => tab.key === activeKey);
  if (activeIndex >= 0) {
    const displaced = visible[visible.length - 1];
    visible[visible.length - 1] = hidden[activeIndex];
    hidden[activeIndex] = displaced;
  }

  return { visible, hidden };
};

export default useTabOverflow;
